import type { DocumentStatus, DocumentType } from '@types';

/** User role gate for lifecycle actions (F1.7). */
export type Role = 'owner' | 'staff';

/**
 * Every mutating lifecycle action the detail view can offer. `edit` covers both
 * a DRAFT free-edit (PUT, no version) and an ISSUED|ACCEPTED edit-and-regenerate
 * (PUT → new R<n> version) — the editor decides which based on status (F5.2/F5.3).
 */
export type LifecycleAction =
  | 'issue'
  | 'edit'
  | 'delete'
  | 'accept'
  | 'decline'
  | 'convert'
  | 'void'
  | 'mark-paid'
  | 'duplicate';

export interface LifecycleContext {
  status: DocumentStatus;
  type: DocumentType;
  role: Role;
  /** Imported docs are view/download-only — no mutations except duplicate (F3.6). */
  imported: boolean;
}

/**
 * Legal lifecycle actions for a document, given its status, type, role, and
 * imported flag. This is the single source of truth the detail view renders
 * buttons from — it must offer ONLY transitions the backend (F5.1) will accept.
 *
 * State machine (F5.1):
 *   both types:  DRAFT → ISSUED;  ISSUED → VOID
 *   quotes:      ISSUED → ACCEPTED | DECLINED | CONVERTED;  ACCEPTED → CONVERTED
 *   invoices:    ISSUED → PAID;  PAID → VOID (owner-only, reason mandatory)
 *
 * Cross-cutting rules:
 *   - Regeneration (edit) is legal ONLY for ISSUED|ACCEPTED and not imported (F5.3).
 *   - Draft edit + delete are legal only in DRAFT (F5.2).
 *   - void from ISSUED is allowed for any role; PAID → VOID is owner-only (F5.4).
 *   - duplicate is always available (copies content into a fresh DRAFT, F3.7) —
 *     it never mutates the source, so it is offered even for imported/terminal docs.
 *   - imported=true blocks every mutation except duplicate (F3.6).
 */
export function legalActions(ctx: LifecycleContext): LifecycleAction[] {
  const { status, type, role, imported } = ctx;

  // Duplicate never mutates the source and is always legal (F3.7).
  const actions = new Set<LifecycleAction>(['duplicate']);

  // Imported records are strictly view/download-only aside from duplicate (F3.6).
  if (imported) {
    return [...actions];
  }

  switch (status) {
    case 'DRAFT':
      actions.add('issue');
      actions.add('edit'); // free draft edit, no version (F5.2)
      actions.add('delete'); // draft-only deletion (F5.2)
      break;

    case 'ISSUED':
      actions.add('edit'); // edit-and-regenerate → R<n> (F5.3)
      actions.add('void'); // ISSUED → VOID, any role (F5.1)
      if (type === 'invoice') {
        actions.add('mark-paid'); // ISSUED → PAID (F5.1)
      } else {
        actions.add('accept'); // ISSUED → ACCEPTED (F5.1)
        actions.add('decline'); // ISSUED → DECLINED (F5.1)
        actions.add('convert'); // ISSUED → CONVERTED (F5.5)
      }
      break;

    case 'ACCEPTED': // quotes only
      actions.add('edit'); // regenerate allowed for ACCEPTED (F5.3)
      actions.add('convert'); // ACCEPTED → CONVERTED (F5.5)
      break;

    case 'PAID': // invoices only
      if (role === 'owner') {
        actions.add('void'); // PAID → VOID, owner-only (F5.4)
      }
      break;

    // DECLINED, CONVERTED, VOID are terminal — duplicate only.
    default:
      break;
  }

  return [...actions];
}

/** Whether the /documents/:id/edit route may load for this document. */
export function canEditDocument(ctx: LifecycleContext): boolean {
  return legalActions(ctx).includes('edit');
}
